/** @jsxImportSource @emotion/react */
import * as R from 'ramda';
import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import * as Buttons from '../../../forms/elements/buttons';
import { Input } from '../../../forms/elements/Input';
import LabelGroup from '../../../forms/elements/LabelGroup';
import ErrorView from '../../../forms/ErrorView';
import { COLUMN_SPACING, ROW_SPACING } from '../../../forms/styles';
import { isNilOrEmpty, assoc } from '../../../language/module';
import { Row } from '../../../layout/flexbox';
import { XLargeText } from '../../../styles/styles';
import { findWaiverSigningError } from './module';
import { EMPTY_SIGNATURE, WaiverSignature } from './waiverSignature';
import { getResource } from '../../../language/resources/getResource';


type Props = {
  version: string;
  onCancel: () => void;
  onSubmit: (signature: WaiverSignature) => void;
  rootCss?: SerializedStyles;
  resources: Record<string, any>
};

type State = {
  signature: WaiverSignature;
  error?: string;
};


/**
 * WaiverSignatureForm: a component that shows a form for the agent to enter details about a guest's waiver signature.
 * Holds the signature details internally until submitting a validated waiver or canceled.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class WaiverSignatureForm extends React.PureComponent<Props, State> {
  public state: State = {
    signature: assoc('version', this.props.version, EMPTY_SIGNATURE),
    error:     undefined,
  };


  private makeChangeHandler = (prop: string) => event => {
    if (!event) {
      return;
    }

    const value = event.target?.value || '';

    this.setState(R.assocPath(['signature', prop], value));
  };


  private onFirstNameChange = this.makeChangeHandler('firstName');
  private onLastNameChange  = this.makeChangeHandler('lastName');
  private onDobChange       = this.makeChangeHandler('dateOfBirth');


  private onCancel = () => this.props.onCancel();


  private onSubmit = event => {
    if (!event) {
      return;
    }

    event.preventDefault();

    const error = findWaiverSigningError(this.props.resources, this.state.signature);
    this.setState(prev => assoc('error', error, prev));

    if (!isNilOrEmpty(error)) {
      return;
    }

    this.props.onSubmit(this.state.signature);
  };


  public componentDidUpdate(prevProps: Props): void {
    if (this.props.version !== prevProps.version) {
      this.setState(R.assocPath(['signature', 'version'], this.props.version));
    }
  }


  public render = () => (
    <form
      css={css`
        ${COLUMN_SPACING};
        ${this.props.rootCss};
      `}
    >
      {/* Form Title */}
      <span css={XLargeText}>{getResource(this.props.resources, 'WAIVER_SIGNATURE_COMPLETED_BY')}</span>

      {/* Form Fields */}
      <LabelGroup label={getResource(this.props.resources, 'WAIVER_SIGNATURE_FIRST_NAME_LABEL')} title={getResource(this.props.resources, 'WAIVER_SIGNATURE_FIRST_NAME_TITLE')}>
        <Input
          autoFocus={true}
          title={getResource(this.props.resources, 'WAIVER_SIGNATURE_FIRST_NAME_INPUT')}
          placeholder={getResource(this.props.resources, 'WAIVER_SIGNATURE_FIRST_NAME_LABEL')}
          value={this.state.signature.firstName}
          onChange={this.onFirstNameChange}
          type='text'
        />
      </LabelGroup>
      <LabelGroup label={getResource(this.props.resources, 'WAIVER_SIGNATURE_LAST_NAME_LABEL')} title={getResource(this.props.resources, 'WAIVER_SIGNATURE_LAST_NAME_TITLE')}>
        <Input
          title={getResource(this.props.resources, 'WAIVER_SIGNATURE_LAST_NAME_INPUT')}
          placeholder={getResource(this.props.resources, 'WAIVER_SIGNATURE_LAST_NAME_LABEL')}
          value={this.state.signature.lastName}
          onChange={this.onLastNameChange}
          type='text'
        />
      </LabelGroup>
      <LabelGroup label={getResource(this.props.resources, 'WAIVER_SIGNATURE_DOB_LABEL')} title={getResource(this.props.resources, 'WAIVER_SIGNATURE_DOB_TITLE')}>
        <Input
          title={getResource(this.props.resources, 'WAIVER_SIGNATURE_DOB_INPUT')}
          placeholder={getResource(this.props.resources, 'WAIVER_SIGNATURE_DOB_LABEL')}
          value={this.state.signature.dateOfBirth.toString()}
          onChange={this.onDobChange}
          type=' date'
        />
      </LabelGroup>

      {/* Validation Errors */}
      <ErrorView error={this.state.error} />

      {/* Submit/Cancel Buttons */}
      <Row justifyContent='flex-end' rootCss={ROW_SPACING}>
        <Buttons.BigSecondary
          type='button'
          onClick={this.onCancel}
          title={getResource(this.props.resources, 'WAIVER_SIGNATURE_CANCEL_TITLE')}
        >
          {getResource(this.props.resources, 'WAIVER_CANCEL')}
        </Buttons.BigSecondary>
        <Buttons.BigNeutral
          type='submit'
          onClick={this.onSubmit}
          title={getResource(this.props.resources, 'WAIVER_SIGNATURE_SUBMIT_TITLE')}
        >
          {getResource(this.props.resources, 'WAIVER_SUBMIT')}
        </Buttons.BigNeutral>
      </Row>
    </form>
  );
}
